<template>
  <div class="stax-form flex flex-col gap-4">
    <PaymentFormInput
      v-model="name"
      label="Cardholder's name"
      :error-message="firstErrorMessage(v$.name)"
      data-cy="name"
    />

    <div class="grid grid-cols-1 md:grid-cols-[1fr_140px_120px] gap-3">
      <div class="flex flex-col gap-1.5">
        <span class="block text-sm font-medium text-gray-900">
          Card number
        </span>
        <div :class="['card-number', 'card-type-' + (cardType || 'none')]">
          <div id="card-number" class="stax-field"></div>
        </div>
      </div>
      <div class="flex flex-col gap-1.5">
        <label
          for="stax-expiration"
          class="block text-sm font-medium text-gray-900 mb-0"
        >
          Expiration
        </label>
        <input
          id="stax-expiration"
          v-model="expiration"
          type="tel"
          placeholder="MM / YYYY"
          data-cy="expiration"
          :class="[
            'stax-field w-full px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none',
            firstErrorMessage(v$.expiration) ? '!border-red-400' : ''
          ]"
          @keypress="exp_typed"
        />
        <span
          v-if="firstErrorMessage(v$.expiration)"
          class="text-xs text-red-600"
          >{{ firstErrorMessage(v$.expiration) }}</span
        >
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="block text-sm font-medium text-gray-900"> CVV </span>
        <div id="card-cvv" class="stax-field"></div>
      </div>
    </div>

    <BillingAddressForm ref="billingAddressForm" :address="billingAddress" />

    <div id="submit" class="hidden"></div>

    <SaveForFuturePayments
      v-if="canSaveCard"
      v-model="saveCard"
      data-cy="stax-card-save-card"
    />
  </div>
</template>

<script>
import { handleStaxError } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import SaveForFuturePayments from "@/components/PaymentModals/SaveForFuturePayments.vue";
import { extractName } from "@/mixins";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import BillingAddressForm from "./BillingAddressForm.vue";
import { loadStax } from "./paymentSdks";
import usePaymentMethods from "./usePaymentMethods";

export default {
  components: {
    BillingAddressForm,
    PaymentFormInput,
    SaveForFuturePayments
  },
  props: {
    autoSaveCard: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  setup() {
    const { billingAddress, paymentMethodsResponse } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      billingAddress,
      v$: useVuelidate(),
      firstErrorMessage
    };
  },
  validations() {
    return {
      name: { required, $autoDirty: true },
      expiration: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      staxJs: null,
      name: "",
      primaryAddress: {},
      expiration: "",
      saveCard: false,
      cardType: ""
    };
  },
  computed: {
    config() {
      return this.paymentMethodsResponse.find(
        (method) => method.key === "stax_card"
      );
    },
    canSaveCard() {
      return !!this.config?.card_savable && !this.autoSaveCard;
    },
    expirationMonth() {
      if (this.expiration) {
        const split = this.expiration.split("/");
        return split.length ? split[0] : "";
      }
      return "";
    },
    expirationYear() {
      if (this.expiration) {
        const split = this.expiration.split("/");
        let year = split.length > 1 ? split[1] : "";
        if (year) {
          const date = new Date(`1/1/${year}`);
          year = date.getFullYear();
        }
        return year;
      }
      return "";
    },
    firstName() {
      return extractName(this.name).firstName;
    },
    lastName() {
      return extractName(this.name).lastName;
    }
  },
  watch: {
    expiration(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.exp_changed(newValue);
      }
    }
  },
  async mounted() {
    try {
      await loadStax();
      if (this.sdkLoadAborted) return;
      this.initForm();
    } catch (error) {
      window.notify.error(
        "Unable to load the payment form. Please refresh and try again."
      );
      window.Sentry?.captureException(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  },
  beforeUnmount() {
    // The SDK load is async; if the form unmounts first, skip initForm so the
    // SDK can't bind its hosted-field iframe onto another form's shared DOM ids.
    this.sdkLoadAborted = true;
  },
  methods: {
    exp_typed(event) {
      const re = RegExp("[0-9/]+");
      if (!re.test(event.key) || event.target.value.length >= 7) {
        event.preventDefault();
      }
    },
    exp_changed(value) {
      if (!value) return;
      this.expiration = value
        .replace(/[^0-9]/g, "")
        .replace(/^([2-9])$/g, "0$1")
        .replace(/^(1{1})([3-9]{1})$/g, "0$1/$2")
        .replace(/^0{1,}/g, "0")
        .replace(/^([0-1]{1}[0-9]{1})([0-9]{1,4}).*/g, "$1/$2");
    },
    async getData() {
      window.notify.close();

      const validated = await this.v$.$validate();
      const billingFormData = await this.$refs.billingAddressForm.getData();

      if (!validated) {
        throw new Error();
      }

      const details = {
        method: "card",
        first_name: this.firstName,
        last_name: this.lastName,
        address_1: billingFormData.street || "",
        address_city: billingFormData.city || "",
        address_state: billingFormData.state || "",
        address_zip: billingFormData.zip_code || "",
        month: this.expirationMonth,
        year: this.expirationYear
      };
      try {
        const response = await this.staxJs.tokenize(details);
        if (!response.has_cvv) {
          throw new Error("Please provide a valid CVV");
        }
        return {
          ...billingFormData,
          name: this.name,
          card_token: response.id,
          card_save_payment_method: this.saveCard || this.autoSaveCard
        };
      } catch (err) {
        handleStaxError(this, err);
      }
    },
    initForm() {
      if (!this.config) {
        return;
      }

      if (this.customer) {
        this.name =
          this.customer.full_name ||
          `${this.customer.first_name} ${this.customer.last_name}`;
        this.primaryAddress =
          this.customer.primary_address ||
          this.customer.addresses.find((address) => address.primary);
      }

      this.staxJs = new StaxJs(this.config.web_id, {
        number: {
          id: "card-number",
          placeholder: "•••• •••• •••• ••••",
          style:
            "padding: 8px 12px; width: 100%; color: #111827; font-size: 14px;",
          type: "text",
          format: "prettyFormat"
        },
        cvv: {
          id: "card-cvv",
          placeholder: "•••",
          style: "padding: 8px 12px; color: #111827; font-size: 14px;",
          type: "text"
        }
      });

      this.staxJs.showCardForm().catch((err) => {
        window.notify.error(err);
        Sentry.captureException(
          err instanceof Error ? err : new Error(String(err))
        );
      });

      this.staxJs.on("card_form_complete", (message) => {
        this.cardType = message.cardType;
      });

      this.staxJs.on("card_form_incomplete", (message) => {
        this.cardType = message.cardType;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.stax-form {
  :deep(iframe) {
    width: 100%;
    min-height: 38px;
  }

  // The host div around each Stax-hosted iframe. The iframe content is
  // styled by Stax via the `style` option in initForm(); this wrapper
  // owns the border/focus state so the field matches PaymentFormInput.
  .stax-field {
    height: 38px;
    background-color: #fff;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    box-shadow: none;
    transition: border-color 150ms, box-shadow 150ms;
  }
  .stax-field:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }

  card-type-none:deep(iframe) {
    background-image: none;
  }

  .card-type-visa:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/visa.svg") no-repeat bottom 9px
      right 5px;
    background-size: 40px;
  }

  .card-type-master:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/mastercard.svg") no-repeat bottom
      9px right 5px;
    background-size: 40px;
  }

  .card-type-discover:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/discover.svg") no-repeat bottom
      9px right 5px;
    background-size: 40px;
  }

  .card-type-american_express:deep(iframe) {
    background: rgba(0, 0, 0, 0) url("@/images/amex.svg") no-repeat bottom 9px
      right 5px;
    background-size: 40px;
  }
}
</style>
